/**
 * Damage numbers (doc 17 §7) — the one effect the doc explicitly allows to be
 * chaotic: jitter, arc, fade.
 *
 * The rule that keeps it from being noise is the aggregation: one number per
 * TARGET per 200 ms, and at most 24 on screen. Forty krill dying does not spawn
 * forty numbers, it spawns however many targets were actually hit — and their
 * damage adds up into one bigger number, which is the number you wanted anyway.
 */

import { font } from '../render/font/font.ts';
import { SEMANTIC } from '../render/palette.ts';
import { hex, PixelSurface } from '../render/surface.ts';

/** Doc 17 §7, verbatim. */
export const AGGREGATE_MS = 200;
export const MAX_ON_SCREEN = 24;
const LIFE_MS = 900;
const RISE_PX = 18;
/** A 1 px drop shadow, so a number stays legible over any world pixel. */
const SHADOW = hex('#010405');

interface Number3D {
  /** Tile-space, so it follows the camera without being re-projected wrong. */
  x: number;
  y: number;
  total: number;
  crit: boolean;
  bornAt: number;
  /** Deterministic lateral drift, so two numbers on one tile do not overlap. */
  drift: number;
}

export class DamageNumbers {
  private live: Number3D[] = [];
  /** Open aggregation windows, by enemy id. */
  private open = new Map<number, Number3D>();
  enabled = true;

  add(enemyId: number, x: number, y: number, dmg: number, crit: boolean, now: number): void {
    if (!this.enabled || dmg <= 0) return;
    const open = this.open.get(enemyId);
    if (open && now - open.bornAt < AGGREGATE_MS) {
      open.total += dmg;
      open.crit ||= crit;
      open.x = x;
      open.y = y;
      return;
    }
    if (this.live.length >= MAX_ON_SCREEN) this.live.shift();
    const n: Number3D = { x, y, total: dmg, crit, bornAt: now, drift: ((enemyId * 37) % 9) - 4 };
    this.live.push(n);
    this.open.set(enemyId, n);
  }

  step(now: number): void {
    if (this.live.length === 0) return;
    this.live = this.live.filter((n) => now - n.bornAt < LIFE_MS);
    for (const [id, n] of this.open) if (now - n.bornAt >= AGGREGATE_MS) this.open.delete(id);
  }

  clear(): void {
    this.live = [];
    this.open.clear();
  }

  get count(): number {
    return this.live.length;
  }

  /**
   * Draw into the UI buffer. `project` turns tile space into UI pixels, which
   * is the run screen's job — this module never learns what a camera is.
   */
  draw(s: PixelSurface, now: number, project: (x: number, y: number) => { x: number; y: number } | null): void {
    if (!this.enabled) return;
    const small = font('micro');
    const big = font('body');
    for (const n of this.live) {
      const age = (now - n.bornAt) / LIFE_MS;
      if (age >= 1) continue;
      const p = project(n.x, n.y);
      if (!p) continue;
      // doc 17 §6: fades happen in STEPS. Four of them, then gone.
      const step = Math.floor((1 - age) * 4);
      if (step <= 0) continue;
      const f = n.crit ? big : small;
      const text = String(Math.round(n.total));
      const w = f.measure(text, { caps: true });
      const colour = n.crit ? SEMANTIC.warning : step >= 3 ? SEMANTIC.ink : SEMANTIC.inkDim;
      f.draw(s, text, Math.round(p.x - w / 2 + n.drift), Math.round(p.y - age * RISE_PX), colour, {
        caps: true,
        shadow: SHADOW,
      });
    }
  }
}
